import { useState } from "react";
import {
  Box,
  Flex,
  Heading,
  HStack,
  Image,
  Text,
  Icon,
  SimpleGrid,
  GridItem,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TiArrowRight } from "react-icons/ti";
import { HiCheck } from "react-icons/hi";
import { HiSparkles } from "react-icons/hi2";

import Logo from "../assets/images/logo.png";
import LogoShort from "../assets/images/logoShort.png";
import { Footer } from "../components/Footer";

const MotionBox = motion(Box);

type PlanId = "starter" | "premium";

const plans = [
  {
    id: "starter" as PlanId,
    name: "Book-d Starter",
    price: "Free",
    period: "",
    description: "Everything you need to get booked",
    cta: "Get Started Free",
    accent: "#D9ED8C",
    accentBorder: "#B8D44A",
    features: [
      "Access to new customers through Book-d marketplace",
      "Unlimited appointment scheduling",
      "Personalised terms and conditions",
      "Automated booking confirmation, reminder and follow-up emails",
      "Booking management (cancel/reschedule)",
      "In-app client messaging",
      "Secure deposit payments via Stripe",
      "10% commission per booking",
    ],
  },
  {
    id: "premium" as PlanId,
    name: "Book-d Premium",
    price: "£9.99",
    period: "per month",
    description: "Tools to grow your brand and income",
    cta: "Start 14-Day Free Trial",
    accent: "#FF8E3C",
    accentBorder: "#E07A2E",
    features: [
      "Everything in Starter, plus:",
      "14 days free trial",
      "0% commission on bookings",
      "Add unlimited team members as your business grows",
      "Performance insights and analytics",
      "Custom promo codes and discounts",
      "Priority business support",
      "Exclusive partner discounts and products",
    ],
  },
];

export const Pricing = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<PlanId>("premium");

  return (
    <Box minH="100vh" bg="#FFFFFF">
      {/* Header */}
      <Box
        bg="linear-gradient(to bottom, #FFF7F3, #D8F1F2)"
        pb={{ base: "60px", lg: "80px" }}
      >
        <Flex
          justify="space-between"
          align="center"
          px={{ base: "16px", lg: "50px" }}
          pt="30px"
          pb={{ base: "40px", lg: "60px" }}
          maxW="1700px"
          mx="auto"
        >
          <HStack gap="8px" cursor="pointer" onClick={() => navigate("/")}>
            <Image
              display={{ base: "block", lg: "none" }}
              src={LogoShort}
              h="40px"
              objectFit="contain"
            />
            <Image
              display={{ base: "none", lg: "block" }}
              src={Logo}
              h="40px"
              objectFit="contain"
            />
          </HStack>

          <HStack gap={0} display={{ base: "none", lg: "flex" }}>
            <Flex
              as="button"
              onClick={() => navigate("/stylist")}
              bg="rgba(0, 0, 0, 0.05)"
              borderTopLeftRadius="30px"
              borderBottomLeftRadius="30px"
              pl="16px"
              pr={7}
              pt="12px"
              pb="8px"
              align="center"
              cursor="pointer"
              _hover={{ bg: "#FFFFFF" }}
              marginRight={-5}
            >
              <Text
                fontSize="16px"
                fontWeight={600}
                color="#19202D"
                fontFamily="LeagueSpartan"
              >
                Book-d Pros
              </Text>
            </Flex>

            <Flex
              as="button"
              onClick={() => navigate("/client")}
              bg="#FF8E3C"
              borderRadius="30px"
              px="16px"
              pt="12px"
              pb="8px"
              align="center"
              cursor="pointer"
              _hover={{ bg: "#D4633D" }}
            >
              <Text
                fontSize="16px"
                fontWeight={600}
                color="#fff"
                fontFamily="LeagueSpartan"
              >
                Get Book-d
              </Text>
            </Flex>
          </HStack>
        </Flex>

        {/* Hero */}
        <Flex flexDir="column" align="center" px="16px">
          <Heading
            fontSize="14px"
            fontWeight={700}
            color="#0A142F"
            fontFamily="LeagueSpartan"
            textTransform="uppercase"
            letterSpacing="2px"
            mb="16px"
          >
            Simple Pricing
          </Heading>

          <Heading
            fontSize={{ base: "32px", lg: "52px" }}
            color="#19202D"
            fontFamily="Comfortaa"
            textAlign="center"
            lineHeight="110%"
            mb="16px"
          >
            Choose Your
            <Box as="br" display={{ base: "block", lg: "none" }} /> Perfect Plan
          </Heading>

          <Text
            fontSize={{ base: "16px", lg: "18px" }}
            color="#5A5365"
            fontWeight={500}
            textAlign="center"
            maxW="560px"
          >
            Elevate your business with Book-d. Start free or unlock premium
            tools to grow your brand and income.
          </Text>
        </Flex>
      </Box>

      {/* Plan Toggle - Mobile */}
      <Flex
        display={{ base: "flex", lg: "none" }}
        justify="center"
        mt="-24px"
        mb="24px"
        px="16px"
      >
        <HStack
          bg="#F5F5F5"
          borderRadius="20px"
          p="4px"
          w="100%"
          maxW="400px"
        >
          {plans.map((plan) => (
            <Flex
              key={plan.id}
              flex={1}
              as="button"
              onClick={() => setSelectedPlan(plan.id)}
              bg={selectedPlan === plan.id ? "#FFFFFF" : "transparent"}
              borderRadius="16px"
              py="12px"
              justify="center"
              align="center"
              cursor="pointer"
              boxShadow={
                selectedPlan === plan.id
                  ? "0 2px 8px rgba(0,0,0,0.08)"
                  : "none"
              }
              transition="all 0.2s"
            >
              <Text
                fontSize="14px"
                fontWeight={600}
                color={selectedPlan === plan.id ? "#19202D" : "#9A9A9A"}
                fontFamily="LeagueSpartan"
              >
                {plan.name}
              </Text>
            </Flex>
          ))}
        </HStack>
      </Flex>

      {/* Pricing Cards */}
      <Box
        px={{ base: "16px", lg: "50px" }}
        mt={{ base: 0, lg: "-40px" }}
        maxW="1100px"
        mx="auto"
      >
        {/* Desktop: Side by side */}
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          gap={{ base: "24px", lg: "32px" }}
        >
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const isPremium = plan.id === "premium";

            return (
              <GridItem
                key={plan.id}
                display={{
                  base: isSelected ? "block" : "none",
                  lg: "block",
                }}
              >
                <AnimatePresence mode="wait">
                  <MotionBox
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      bg="#FFFFFF"
                      borderRadius="32px"
                      borderWidth="2px"
                      borderColor={
                        isPremium ? plan.accent : "rgba(0,0,0,0.08)"
                      }
                      overflow="hidden"
                      boxShadow={
                        isPremium
                          ? "0 8px 40px rgba(255, 142, 60, 0.15)"
                          : "0 4px 24px rgba(0,0,0,0.06)"
                      }
                      position="relative"
                    >
                      {/* Premium badge */}
                      {isPremium && (
                        <Flex
                          bg="#FF8E3C"
                          justify="center"
                          align="center"
                          gap="6px"
                          py="8px"
                        >
                          <Icon size="sm" color="#FFF">
                            <HiSparkles />
                          </Icon>
                          <Text
                            fontSize="13px"
                            fontWeight={700}
                            color="#FFF"
                            fontFamily="LeagueSpartan"
                            letterSpacing="1px"
                            textTransform="uppercase"
                          >
                            Most Popular
                          </Text>
                          <Icon size="sm" color="#FFF">
                            <HiSparkles />
                          </Icon>
                        </Flex>
                      )}

                      <Box p={{ base: "28px", lg: "36px" }}>
                        {/* Plan Name */}
                        <Text
                          fontSize={{ base: "24px", lg: "28px" }}
                          fontWeight={700}
                          color={isPremium ? "#FF8E3C" : "#19202D"}
                          fontFamily="LeagueSpartan"
                          mb="4px"
                        >
                          {plan.name}
                        </Text>

                        {/* Price */}
                        <Flex align="baseline" gap="4px" mb="8px">
                          <Heading
                            fontSize={{ base: "44px", lg: "52px" }}
                            fontWeight={700}
                            color="#19202D"
                            fontFamily="LeagueSpartan"
                            lineHeight="100%"
                          >
                            {plan.price}
                          </Heading>
                          {plan.period && (
                            <Text
                              fontSize="16px"
                              color="#9A9A9A"
                              fontWeight={500}
                            >
                              {plan.period}
                            </Text>
                          )}
                        </Flex>

                        {/* Description */}
                        <Text
                          fontSize="15px"
                          color="#5A5365"
                          fontWeight={500}
                          mb="28px"
                        >
                          {plan.description}
                        </Text>

                        {/* CTA Button */}
                        <Flex
                          as="button"
                          w="100%"
                          bg={isPremium ? "#FF8E3C" : "#19202D"}
                          borderRadius="16px"
                          py="16px"
                          justify="center"
                          align="center"
                          gap="8px"
                          cursor="pointer"
                          _hover={{
                            bg: isPremium ? "#E07A2E" : "#2A3344",
                          }}
                          transition="background 0.2s"
                          mb="32px"
                          onClick={() =>
                            window.open(
                              "https://calendly.com/book-d/book-d-set-up",
                              "_blank",
                            )
                          }
                        >
                          <Text
                            fontSize="18px"
                            fontWeight={600}
                            color="#FFFFFF"
                            fontFamily="LeagueSpartan"
                          >
                            {plan.cta}
                          </Text>
                          <Icon size="lg" color="#FFF">
                            <TiArrowRight />
                          </Icon>
                        </Flex>

                        {/* Divider */}
                        <Box h="1px" bg="rgba(0,0,0,0.06)" mb="28px" />

                        {/* Section header */}
                        <Text
                          fontSize="13px"
                          fontWeight={700}
                          color="#9A9A9A"
                          fontFamily="LeagueSpartan"
                          textTransform="uppercase"
                          letterSpacing="1.5px"
                          mb="20px"
                        >
                          What's included
                        </Text>

                        {/* Features */}
                        <Flex flexDir="column" gap="16px">
                          {plan.features.map((feature, idx) => (
                            <HStack key={idx} gap="12px" align="flex-start">
                              <Flex
                                w="24px"
                                h="24px"
                                minW="24px"
                                borderRadius="full"
                                bg={
                                  isPremium
                                    ? "rgba(255, 142, 60, 0.12)"
                                    : "rgba(217, 237, 140, 0.5)"
                                }
                                align="center"
                                justify="center"
                                mt="1px"
                              >
                                <Icon
                                  size="sm"
                                  color={isPremium ? "#FF8E3C" : "#6B8A1A"}
                                >
                                  <HiCheck />
                                </Icon>
                              </Flex>
                              <Text
                                fontSize="15px"
                                color="#19202D"
                                fontWeight={500}
                                lineHeight="140%"
                              >
                                {feature}
                              </Text>
                            </HStack>
                          ))}
                        </Flex>
                      </Box>
                    </Box>
                  </MotionBox>
                </AnimatePresence>
              </GridItem>
            );
          })}
        </SimpleGrid>
      </Box>

      {/* Commission note */}
      <Flex justify="center" px="16px" mt="32px" mb="20px">
        <Box
          bg="rgba(217, 237, 140, 0.2)"
          borderRadius="16px"
          px="24px"
          py="16px"
          maxW="700px"
          w="100%"
        >
          <Text
            fontSize="14px"
            color="#5A5365"
            fontWeight={500}
            textAlign="center"
          >
            <Text as="span" fontWeight={700} color="#19202D">
              Starter plan note:
            </Text>{" "}
            10% commission applies to bookings received via the Book-d
            directory. Bring your own clients? No commission at all.
          </Text>
        </Box>
      </Flex>

      {/* CTA Section */}
      <Box
        px={{ base: "16px", lg: "50px" }}
        py={{ base: "60px", lg: "80px" }}
        maxW="1100px"
        mx="auto"
      >
        <Box
          bg="rgba(217, 237, 140, 0.3)"
          borderRadius="32px"
          py={{ base: "40px", lg: "60px" }}
          px={{ base: "24px", lg: "60px" }}
          textAlign="center"
        >
          <Heading
            fontSize={{ base: "28px", lg: "40px" }}
            color="#19202D"
            fontFamily="LeagueSpartan"
            fontWeight={700}
            mb="16px"
            lineHeight="110%"
          >
            Ready to grow your business?
          </Heading>

          <Text
            fontSize={{ base: "16px", lg: "18px" }}
            color="#5A5365"
            fontWeight={500}
            mb="32px"
            maxW="480px"
            mx="auto"
          >
            Join hundreds of Black hairstylists already using Book-d to manage
            bookings, get paid, and attract new clients.
          </Text>

          <Flex justify="center" gap="16px" flexWrap="wrap">
            <Flex
              as="button"
              bg="#19202D"
              borderRadius="30px"
              px="28px"
              py="14px"
              align="center"
              gap="8px"
              cursor="pointer"
              _hover={{ bg: "#2A3344" }}
              onClick={() =>
                window.open(
                  "https://calendly.com/book-d/book-d-set-up",
                  "_blank",
                )
              }
            >
              <Text
                fontSize="18px"
                fontWeight={600}
                color="#fff"
                fontFamily="LeagueSpartan"
              >
                Schedule a Demo
              </Text>
              <Icon size="lg" color="#FFF">
                <TiArrowRight />
              </Icon>
            </Flex>
          </Flex>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};
